/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';

import { navigate } from '../../helpers/history';
import { pruneSelection } from './helpers/applySelection';
import { buildSelectionKey } from './helpers/selectionKey';

export const removeMasterProduct = createAsyncThunk(
  'products/removeMasterProduct',
  async ({ productId, listingIds }, {
    extra: {
      sdk, Logger, utils, showErrorDialog,
    },
    getState,
  }) => {
    try {
      const state = getState();
      const product = state.products.items.find(({ _id }) => _id === productId);
      const activeSalesChannelIds = new Set(
        (state.user.team?.salesChannels || []).map((salesChannel) => String(salesChannel._id)),
      );
      const productsState = state.products;
      const activeListingIds = listingIds.filter((listingId) => {
        const listing = Object.values(productsState.entities?.listings || {}).find(
          (entity) => String(entity._id) === String(listingId),
        );
        const salesChannelId = listing?.salesChannelId;
        return !salesChannelId || activeSalesChannelIds.has(String(salesChannelId));
      });

      await sdk.products.deleteMasterProduct(productId, activeListingIds.length ? activeListingIds : undefined);

      if (getState().router?.location?.pathname?.includes(productId)) {
        navigate('/products');
      }

      const listingProductIds = Object.values(productsState.entities?.listings || {})
        .filter((listing) => listing.masterProductId === productId)
        .map((listing) => listing._id)
        .filter(Boolean);

      return { productId, listingProductIds };
    } catch (error) {
      const errorMessage = utils.getDataFromResponceError(error, 'msg') || 'Can not remove product';
      showErrorDialog(errorMessage);
      Logger.error(new Error('Error product removing'), { error }, [
        'RemoveProductFailed',
        { errorMessage: errorMessage || 'NoMessage' },
      ]);
      throw error;
    }
  },
);

export const reducer = (builder) => {
  builder
    .addCase(
      removeMasterProduct.pending,
      (state, { meta }) => {
        state.items.forEach((product) => {
          if (product._id === meta.arg.productId) {
            product.isBusy = true;
          }
        });
      },
    )
    .addCase(
      removeMasterProduct.fulfilled,
      (state, { payload }) => {
        const { productId, listingProductIds } = payload;
        const masterId = String(productId);
        const listingSet = new Set(listingProductIds.map(String));

        state.items = state.items.filter(({ _id }) => _id !== productId);

        const prevLength = state.search.items.length;
        state.search.items = state.search.items.filter((item) => {
          const id = String(item._id);
          const base = item.baseProductId != null ? String(item.baseProductId) : '';
          const master = item.masterProductId != null ? String(item.masterProductId) : '';

          return id !== masterId
            && base !== masterId
            && master !== masterId
            && !listingSet.has(id)
            && !listingSet.has(base);
        });
        const removedCount = prevLength - state.search.items.length;
        if (removedCount > 0) {
          state.search.total = Math.max(0, (state.search.total || 0) - removedCount);
        }

        // Deleting a master can cascade-remove its variants from search.items too — prune any
        // selection key whose row is now gone, not just the master's own key.
        const remainingKeys = new Set(state.search.items.map(buildSelectionKey));
        const staleKeys = state.ui.selection.keys.filter((key) => !remainingKeys.has(key));
        state.ui.selection = pruneSelection(state.ui.selection, staleKeys);

        state.error = null;
      },
    )
    .addCase(
      removeMasterProduct.rejected,
      (state, { meta, error }) => {
        state.items.forEach((product) => {
          if (product._id === meta.arg.productId) delete product.isBusy;
        });
        state.error = error;
      },
    );
};
